import React, { useMemo } from "react";
import { CalendarDays, Copy, Plus, Trash2 } from "lucide-react";

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const toDateInput = (date) => date.toISOString().slice(0, 10);

const QUICK_PRESETS = [
  {
    id: "all-day",
    label: "All day",
    helper: "12am - 11:59pm",
    enabled: true,
    slots: [{ start: "00:00", end: "23:59" }],
  },
  {
    id: "morning",
    label: "6hrs morning",
    helper: "6am - 12pm",
    enabled: true,
    slots: [{ start: "06:00", end: "12:00" }],
  },
  {
    id: "afternoon",
    label: "6hrs afternoon",
    helper: "12pm - 6pm",
    enabled: true,
    slots: [{ start: "12:00", end: "18:00" }],
  },
  {
    id: "evening",
    label: "6hrs evening",
    helper: "6pm - 11:59pm",
    enabled: true,
    slots: [{ start: "18:00", end: "23:59" }],
  },
  {
    id: "night",
    label: "6hrs night",
    helper: "12am - 6am",
    enabled: true,
    slots: [{ start: "00:00", end: "06:00" }],
  },
  {
    id: "unavailable",
    label: "Not available",
    helper: "No patients",
    enabled: false,
    slots: [{ start: "", end: "" }],
  },
];

const cloneSlots = (slots = []) =>
  slots.map((slot) => ({
    start: slot.start || "",
    end: slot.end || "",
  }));

export const buildOneWeekCalendar = (source = [], options = {}) => {
  const markExisting = Boolean(options.markExisting);
  const byDate = new Map(
    (Array.isArray(source) ? source : []).map((item) => [
      String(item?.date || "").slice(0, 10),
      item,
    ])
  );

  return Array.from({ length: 7 }).map((_, index) => {
    const date = new Date();
    date.setHours(12, 0, 0, 0);
    date.setDate(date.getDate() + index);

    const dateKey = toDateInput(date);
    const existing = byDate.get(dateKey) || {};
    const slots = Array.isArray(existing.slots) ? existing.slots : [];
    const hasExistingDay = byDate.has(dateKey);

    return {
      date: dateKey,
      day: existing.day || DAY_NAMES[date.getDay()],
      enabled: existing.enabled === false ? false : slots.length > 0,
      slots: slots.length ? slots : [{ start: "", end: "" }],
      note: existing.note || "",
      configured:
        existing.configured === true ||
        slots.some((slot) => slot.start && slot.end) ||
        (markExisting && hasExistingDay),
    };
  });
};

export const isAvailabilityDaySet = (day = {}) => {
  if (!day.configured) return false;
  if (day.enabled === false) return true;

  const slots = Array.isArray(day.slots) ? day.slots : [];
  const filledSlots = slots.filter((slot) => slot.start && slot.end);

  return (
    filledSlots.length > 0 &&
    slots.every((slot) => Boolean(slot.start) && Boolean(slot.end))
  );
};

export const getAvailabilityWeekProgress = (calendar = []) => {
  const week = buildOneWeekCalendar(calendar);
  const completeDays = week.filter(isAvailabilityDaySet).length;

  return {
    completeDays,
    totalDays: week.length,
    isComplete: completeDays === week.length,
  };
};

export const isAvailabilityWeekComplete = (calendar = []) =>
  getAvailabilityWeekProgress(calendar).isComplete;

export const sanitizeCalendarForSave = (calendar = []) =>
  buildOneWeekCalendar(calendar).map((day) => ({
    date: day.date,
    day: day.day,
    enabled: day.enabled,
    slots:
      day.enabled === false
        ? []
        : (day.slots || [])
            .filter((slot) => slot.start && slot.end)
            .map((slot) => ({ start: slot.start, end: slot.end })),
    note: day.enabled === false ? "" : day.note || "",
  }));

const formatDate = (value) => {
  if (!value) return "";
  return new Date(`${value}T12:00:00`).toLocaleDateString("en-NG", {
    day: "numeric",
    month: "short",
  });
};

const AvailabilityCalendarEditor = ({
  value = [],
  onChange,
  readOnly = false,
}) => {
  const calendar = useMemo(() => buildOneWeekCalendar(value), [value]);
  const progress = getAvailabilityWeekProgress(calendar);

  const updateDay = (dayIndex, updates) => {
    const next = calendar.map((item, index) =>
      index === dayIndex ? { ...item, configured: true, ...updates } : item
    );
    onChange?.(next);
  };

  const updateSlot = (dayIndex, slotIndex, field, fieldValue) => {
    const day = calendar[dayIndex];
    const slots = [...(day.slots || [])];
    slots[slotIndex] = { ...(slots[slotIndex] || {}), [field]: fieldValue };
    updateDay(dayIndex, { slots, enabled: true, configured: true });
  };

  const addSlot = (dayIndex) => {
    const day = calendar[dayIndex];
    updateDay(dayIndex, {
      enabled: true,
      slots: [...(day.slots || []), { start: "", end: "" }],
      configured: true,
    });
  };

  const removeSlot = (dayIndex, slotIndex) => {
    const day = calendar[dayIndex];
    const slots = (day.slots || []).filter((_, index) => index !== slotIndex);
    updateDay(dayIndex, {
      slots: slots.length ? slots : [{ start: "", end: "" }],
      enabled: slots.length > 0,
      configured: true,
    });
  };

  const applyPreset = (dayIndex, preset) => {
    updateDay(dayIndex, {
      enabled: preset.enabled,
      slots: cloneSlots(preset.slots),
      note: preset.enabled ? calendar[dayIndex]?.note || "" : "",
      configured: true,
    });
  };

  const applyPresetToAllDays = (preset) => {
    const next = calendar.map((day) => ({
      ...day,
      enabled: preset.enabled,
      slots: cloneSlots(preset.slots),
      note: preset.enabled ? day.note || "" : "",
      configured: true,
    }));

    onChange?.(next);
  };

  const copyDayToAllDays = (dayIndex) => {
    const sourceDay = calendar[dayIndex];
    const next = calendar.map((day) => ({
      ...day,
      enabled: sourceDay.enabled,
      slots: cloneSlots(sourceDay.slots),
      note: sourceDay.enabled ? sourceDay.note || "" : "",
      configured: true,
    }));

    onChange?.(next);
  };

  return (
    <div className="grid grid-cols-1 gap-3">
      {!readOnly && (
        <section className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-950">
                Quick set all days
              </p>
              <p className="mt-1 text-xs leading-5 text-slate-500">
                Pick one option to fill the whole week. You can still edit any
                day after.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {QUICK_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => applyPresetToAllDays(preset)}
                  className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:border-slate-300 hover:bg-white"
                >
                  <span className="block">{preset.label}</span>
                  <span className="mt-0.5 block text-[11px] font-medium text-slate-400">
                    {preset.helper}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div
            className={`mt-4 rounded-xl px-3 py-2 text-xs font-medium ${
              progress.isComplete
                ? "bg-emerald-50 text-emerald-700"
                : "bg-amber-50 text-amber-700"
            }`}
          >
            {progress.isComplete
              ? "All 7 days are set. You can save the calendar."
              : `${progress.completeDays}/${progress.totalDays} days set. Set every day before saving.`}
          </div>
        </section>
      )}

      {calendar.map((day, dayIndex) => {
        const activeSlots = (day.slots || []).filter(
          (slot) => slot.start && slot.end
        );
        const dayIsSet = isAvailabilityDaySet(day);

        return (
          <section
            key={day.date}
            className="rounded-2xl border border-slate-200 bg-white p-4"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                  <CalendarDays size={17} />
                </div>

                <div>
                  <p className="text-sm font-semibold text-slate-950">
                    {day.day}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {formatDate(day.date)}
                  </p>
                </div>
              </div>

              {readOnly ? (
                <span
                  className={`w-fit rounded-full px-3 py-1 text-xs font-semibold ${
                    day.enabled && activeSlots.length
                      ? "bg-emerald-50 text-emerald-700"
                      : day.configured
                      ? "bg-slate-100 text-slate-600"
                      : "bg-slate-100 text-slate-500"
                  }`}
                >
                  {day.enabled && activeSlots.length
                    ? "Available"
                    : day.configured
                    ? "Unavailable"
                    : "Not set"}
                </span>
              ) : (
                <label className="flex w-fit cursor-pointer items-center gap-2 rounded-full border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={day.enabled}
                    onChange={(event) =>
                      updateDay(dayIndex, { enabled: event.target.checked })
                    }
                    className="accent-[#075E54]"
                  />
                  Available
                </label>
              )}
            </div>

            {readOnly ? (
              <div className="mt-4">
                {day.enabled && activeSlots.length ? (
                  <div className="flex flex-wrap gap-2">
                    {activeSlots.map((slot, index) => (
                      <span
                        key={`${slot.start}-${slot.end}-${index}`}
                        className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700"
                      >
                        {slot.start} - {slot.end}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No time selected.</p>
                )}

                {day.note && (
                  <p className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">
                    {day.note}
                  </p>
                )}
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                  {QUICK_PRESETS.map((preset) => (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => applyPreset(dayIndex, preset)}
                      className={`rounded-xl border px-3 py-2 text-left text-xs font-semibold ${
                        preset.enabled === false
                          ? "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                          : "border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-white"
                      }`}
                    >
                      <span className="block">{preset.label}</span>
                      <span className="mt-0.5 block text-[11px] font-medium text-slate-400">
                        {preset.helper}
                      </span>
                    </button>
                  ))}
                </div>

                {(day.slots || []).map((slot, slotIndex) => (
                  <div
                    key={slotIndex}
                    className="grid grid-cols-[1fr_1fr_auto] gap-2"
                  >
                    <input
                      type="time"
                      value={slot.start || ""}
                      disabled={!day.enabled}
                      onChange={(event) =>
                        updateSlot(dayIndex, slotIndex, "start", event.target.value)
                      }
                      className="h-11 rounded-xl border border-slate-200 px-3 text-sm text-slate-800 outline-none disabled:bg-slate-50"
                    />

                    <input
                      type="time"
                      value={slot.end || ""}
                      disabled={!day.enabled}
                      onChange={(event) =>
                        updateSlot(dayIndex, slotIndex, "end", event.target.value)
                      }
                      className="h-11 rounded-xl border border-slate-200 px-3 text-sm text-slate-800 outline-none disabled:bg-slate-50"
                    />

                    <button
                      type="button"
                      onClick={() => removeSlot(dayIndex, slotIndex)}
                      disabled={!day.enabled}
                      className="flex h-11 w-11 items-center justify-center rounded-xl text-red-500 hover:bg-red-50 disabled:text-slate-300"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}

                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => addSlot(dayIndex)}
                      disabled={!day.enabled}
                      className="inline-flex h-10 w-fit items-center gap-2 rounded-xl border border-slate-200 px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:text-slate-300"
                    >
                      <Plus size={15} />
                      Add time
                    </button>

                    <button
                      type="button"
                      onClick={() => copyDayToAllDays(dayIndex)}
                      disabled={!dayIsSet}
                      className="inline-flex h-10 w-fit items-center gap-2 rounded-xl border border-slate-200 px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:text-slate-300"
                    >
                      <Copy size={15} />
                      Use this for all days
                    </button>
                  </div>

                  <input
                    value={day.note || ""}
                    disabled={!day.enabled}
                    onChange={(event) =>
                      updateDay(dayIndex, { note: event.target.value })
                    }
                    placeholder="Optional note"
                    className="h-10 min-w-0 rounded-xl border border-slate-200 px-3 text-sm text-slate-800 outline-none disabled:bg-slate-50 sm:w-56"
                  />
                </div>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default AvailabilityCalendarEditor;
